import { faPen, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelButtonGrid from '../../../components/voxel/Buttons/VoxelButtonGrid';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelDialogActionButton from '../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';

export interface EditTagGroupProps {
  project: ProjectGq;
  reload: () => Promise<unknown>;
  tagGroup: TagGroupGq;
  setSelectedTagGroupId?: (id: number) => void;
}

export const EditTagGroup: React.FC<EditTagGroupProps> = ({
  project,
  tagGroup,
  setSelectedTagGroupId,
  reload,
}) => {
  // Popover
  const [openPopover, setOpenPopover] = useState(false);

  // Editing
  const [name, setName] = useState(tagGroup?.name ?? '');
  useEffect(() => setName(tagGroup?.name ?? ''), [tagGroup?.id]);
  const [saving, setSaving] = useState(false);
  const tagGroupMutation = projectsApiService.mutations.tags.useEditTagGroupName();

  const handleSave = () => {
    setSaving(true);
    tagGroupMutation({
      name: name,
      projectId: tagGroup.projectId,
      id: tagGroup.id,
    }).finally(() => {
      setSaving(false);
      setOpenPopover(false);
    });
  };

  // Deleting
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const deleteMutation = projectsApiService.mutations.tags.useDeleteTagGroup();
  const [deleting, setDeleting] = useState(false);
  const handleDelete = () => {
    setDeleting(true);
    deleteMutation({ projectId: tagGroup.projectId, id: tagGroup.id })
      .then((result) =>
        reload().then(() =>
          !!project.tagGroups?.length ? setSelectedTagGroupId(project.tagGroups[0].id) : undefined
        )
      )
      .then(reload)
      .finally(() => {
        setDeleting(false);
        setOpenPopover(false);
      });
  };

  // Switch back view upon closing
  useEffect(() => {
    !openPopover && setShowDeleteDialog(false);
  }, [openPopover]);

  return (
    <VoxelPopover open={openPopover} onOpenChange={setOpenPopover}>
      <VoxelPopoverTrigger enableActive>
        <VoxelIconButton
          icon={<FontAwesomeIcon icon={faPen} />}
          color={openPopover ? 'primary' : 'transparent'}
          className="mb-0.5"
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent className="w-[330px]">
        <div className="absolute right-4 top-2">
          <VoxelIconButton
            icon={<FontAwesomeIcon icon={faTimes} />}
            color="white"
            onClick={() => setOpenPopover(false)}
          />
        </div>
        {!showDeleteDialog && (
          <form
            className="p-6 flex flex-col gap-6"
            onSubmit={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <VoxelInput
              label="Tag Group name"
              labelPosition="top"
              autoFocus
              size="sm"
              value={name ?? ''}
              onChange={(event) =>
                setName(
                  event.target.value?.slice(0, 1).toUpperCase() +
                    event.target.value?.slice(1).toLowerCase()
                )
              }
            />
            <div />
            <div className="flex flex-row justify-end gap-2">
              <VoxelButton
                title="Delete tag group"
                color="error"
                onClick={() => setShowDeleteDialog(true)}
                size="sm"
                disabled={saving || deleting}
              />
              <VoxelButton
                title="Save changes"
                color="primary"
                onClick={handleSave}
                size="sm"
                type="submit"
                disabled={saving || deleting || name === tagGroup.name}
              />
            </div>
          </form>
        )}
        {showDeleteDialog && (
          <div className="p-6 flex flex-col gap-4">
            <VoxelText>This will delete the tag group and its tags</VoxelText>
            <VoxelButtonGrid>
              <VoxelDialogActionButton
                title="Cancel"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setOpenPopover(false);
                }}
                color="gray"
                fullWidth
                size="sm"
              />
              <VoxelDialogActionSubmit
                color="error"
                title="Delete"
                submitting={deleting}
                onClick={handleDelete}
                fullWidth
                size="sm"
              />
            </VoxelButtonGrid>
          </div>
        )}
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default EditTagGroup;
